<script setup>
import { computed, watch, ref } from 'vue';
import { useStore } from 'vuex';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { messageStamp } from 'shared/helpers/timeHelper';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import BubbleImage from 'dashboard/components/widgets/conversation/bubble/Image.vue';
import Dialog from "dashboard/components-next/dialog/Dialog.vue";
import {
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper.js';
import UserWithdrawDialog from "./UserWithdrawDialog.vue";

const { id } = defineProps({
  id: {
    type: Number,
    default: null,
  },
});

const store = useStore();
const fetchUserDetails = async () => {
  await store.dispatch('giftCards/getMemberDetails', {
    user_id: id,
  });
};

const uiFlags = useMapGetter('giftCards/getUIFlags');
const getMemberDetails = useMapGetter('giftCards/getMemberDetails');
const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');
const fetchingUserDetails = computed(() => uiFlags.value.fetchingMemberDetails);
const userDetails = computed(() => getMemberDetails.value);
const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const showWithdrawButton = computed(() => {
  return !hasPermissions(['encrypt_message'], userPermissions.value) && userDetails.value.user_info.money > 1;
});

const userInfoDialogRef = ref(null);
const refUserWithdrawDialog = ref(null);

const userDetailsClose = () => {
  console.log('close');
};

const handleWithdraw = () => {
  refUserWithdrawDialog.value?.withdrawDialogRef.open();
};

watch(
  () => id,
  (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      fetchUserDetails(newVal);
    }
  },
  { immediate: true }
);

const displayedTransactions = computed(() => {
  return userDetails.value.money_list.slice(0, 5);
});

// 计算属性：只显示最近5条订单
const displayedOrders = computed(() => {
  return userDetails.value.order_list.slice(0, 5);
});

const formattedDate = time => {
  return time > 0 ? messageStamp(time, 'Y LLL d, h:mm a') : '--';
};

defineExpose({
  userInfoDialogRef,
});
</script>

<template>
  <Dialog
    ref="userInfoDialogRef"
    type="edit"
    title="User Info"
    overflow-y-auto
    width="2xl"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="userDetailsClose"
  >
    <!-- Loading Spinner -->
    <div
      v-if="fetchingUserDetails"
      class="flex items-center justify-center h-60"
    >
      <Spinner class="w-12 h-12 text-blue-600" />
    </div>

    <!-- Content Container -->
    <div v-if="!fetchingUserDetails" class="space-y-6 max-h-[70vh] overflow-y-auto px-2">
      <!-- User Information Card -->
      <div class="bg-white rounded-lg shadow-sm">
        <div class="bg-gradient-to-r from-blue-50 to-white px-6 py-4">
          <h3 class="text-lg font-medium text-gray-800">User Details</h3>
        </div>
        <div class="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="flex items-center space-x-3">
            <span class="text-gray-500">ID:</span>
            <span class="font-medium">{{ userDetails.user_info.id }}</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="text-gray-500">Username:</span>
            <span class="font-medium">{{ userDetails.user_info.username }}</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="text-gray-500">Country:</span>
            <span class="font-medium">{{ userDetails.user_info.country_name }}</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="text-gray-500">Brand:</span>
            <span class="font-medium">{{ userDetails.user_info.brand_name }}</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="text-gray-500">VIP Level:</span>
            <span class="font-medium">{{ userDetails.user_info.vip_level }}</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="text-gray-500">VIP Exp:</span>
            <span class="font-medium">{{ userDetails.user_info.vip_exp }}</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="text-gray-500">Rebate:</span>
            <span class="font-medium">{{ userDetails.user_info.rebate_money }}</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="text-gray-500">Balance:</span>
            <span class="font-medium"
              >{{ userDetails.user_info.currency_symbol
              }}{{ userDetails.user_info.money }}</span
            >
            <div v-if="showWithdrawButton" class="relative flex justify-end w-full gap-2">
              <woot-button color-scheme="primary" size="tiny" @click="handleWithdraw">
                Withdraw
              </woot-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Transactions Card -->
      <div
        v-if="userDetails.money_list.length"
        class="bg-white rounded-lg shadow-sm"
      >
        <div class="bg-gradient-to-r from-blue-50 to-white px-6 py-4 flex justify-between items-center">
          <h3 class="text-lg font-medium text-gray-800">Recent Transactions</h3>
          <span class="text-sm text-gray-500">
            Showing latest {{ Math.min(5, userDetails.money_list.length) }} of {{ userDetails.money_list.length }}
          </span>
        </div>
        <div class="divide-y divide-gray-100">
          <div
            v-for="(transaction, index) in displayedTransactions"
            :key="index"
            class="px-6 py-4 hover:bg-gray-50 transition-colors"
          >
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="flex items-center space-x-3">
                <span class="text-gray-500">Type:</span>
                <span class="font-medium">{{ transaction.type }}</span>
              </div>
              <div class="flex items-center space-x-3">
                <span class="text-gray-500">Amount:</span>
                <span class="font-medium text-blue-600">{{ transaction.amount }}</span>
              </div>
              <div class="flex items-center space-x-3">
                <span class="text-gray-500">No:</span>
                <span class="font-medium">{{ transaction.order_no }}</span>
              </div>
              <div class="flex items-center space-x-3">
                <span class="text-gray-500">Time:</span>
                <span class="font-medium">{{ formattedDate(transaction.create_time) }}</span>
              </div>
            </div>
            <div class="mt-2 text-sm text-gray-500">
              Memo: {{ transaction.memo }}
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Orders Card -->
      <div
        v-if="userDetails.order_list.length"
        class="bg-white rounded-lg shadow-sm"
      >
        <div class="bg-gradient-to-r from-blue-50 to-white px-6 py-4 flex justify-between items-center">
          <h3 class="text-lg font-medium text-gray-800">Recent Orders</h3>
          <span class="text-sm text-gray-500">
            Showing latest {{ Math.min(5, userDetails.order_list.length) }} of {{ userDetails.order_list.length }}
          </span>
        </div>
        <div class="divide-y divide-gray-100">
          <div
            v-for="(order, index) in displayedOrders"
            :key="index"
            class="px-6 py-4 hover:bg-gray-50 transition-colors"
          >
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="flex items-center space-x-3">
                <span class="text-gray-500">Order No:</span>
                <span class="font-medium">{{ order.order_no }}</span>
              </div>
              <div class="flex items-center space-x-3">
                <span class="text-gray-500">Status:</span>
                <span
                  class="px-2 py-1 rounded-full text-sm"
                  :class="{
                    'bg-green-100 text-green-700': order.status === 'completed',
                    'bg-yellow-100 text-yellow-700': order.status === 'pending',
                    'bg-red-100 text-red-700': order.status === 'cancelled'
                  }"
                >
                  {{ order.status }}
                </span>
              </div>
              <div class="flex items-center space-x-3">
                <span class="text-gray-500">Purchase Amount:</span>
                <span class="font-medium text-blue-600">{{ order.purchase_amount }}</span>
              </div>
              <div class="flex items-center space-x-3">
                <span class="text-gray-500">Sellout Amount:</span>
                <span class="font-medium text-blue-600">{{ order.sellout_amount }}</span>
              </div>
              <div class="flex items-center space-x-3">
                <span class="text-gray-500">Time:</span>
                <span class="font-medium">{{ formattedDate(order.deal_start_time) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>

  <UserWithdrawDialog
    :user-info="userDetails.user_info"
    ref="refUserWithdrawDialog"
  />
</template>
